// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Alert, Button, Dropdown, FormInput, Spinner } from '@loa/ui-components';
import { financialPositionApi, type FinancialGoal } from '../../api/financialPositionApi';

interface Props {
  profileId: string;
}

const GOAL_TYPES = ['RETIREMENT', 'EDUCATION', 'PROPERTY', 'MAJOR_PURCHASE', 'LEGACY', 'OTHER'];
const PRIORITIES = ['HIGH', 'MEDIUM', 'LOW'];

/** An empty draft for the "Add goal" form. */
function blankDraft(): FinancialGoal {
  return {
    id: null,
    title: null,
    goalType: 'OTHER',
    priority: 'MEDIUM',
    targetAmount: null,
    targetDate: null,
    status: 'ACTIVE',
  };
}

function fmtMoney(value: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

function titleCase(value: string | null): string {
  if (!value) return '—';
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');
}

/**
 * Advisor-managed financial goals (gap A5 of cashflow-model-gap-analysis.md —
 * the orphaned Fact-Find goals capture was replaced by this CRUD). Goal lump
 * sums land as one-off expenditure in the relationship cashflow's target year.
 * Mounted on the relationship Financial Position tab beside the inheritances
 * panel.
 */
export function FinancialGoalsPanel({ profileId }: Props) {
  const [goals, setGoals] = useState<FinancialGoal[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = (cancelledRef?: { cancelled: boolean }) =>
    financialPositionApi
      .listGoals(profileId)
      .then((rows) => {
        if (!cancelledRef?.cancelled) setGoals(rows);
      })
      .catch(() => {
        if (!cancelledRef?.cancelled) setError('Could not load financial goals.');
      })
      .finally(() => {
        if (!cancelledRef?.cancelled) setLoading(false);
      });

  const reload = () => {
    setLoading(true);
    setError(null);
    void load();
  };

  useEffect(() => {
    const ref = { cancelled: false };
    setLoading(true);
    setError(null);
    void load(ref);
    return () => {
      ref.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  if (loading) return <Spinner />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!goals) return null;

  return (
    <div
      data-testid="financial-goals-panel"
      className="flex flex-col w-full rounded-card bg-white shadow-card p-lg gap-md"
    >
      <div className="text-black text-body-01 font-medium leading-normal font-sans">
        Financial goals
      </div>

      {goals.length === 0 ? (
        <div data-testid="financial-goals-empty" className="text-body-03 text-grey-04">
          No financial goals captured for this relationship yet.
        </div>
      ) : (
        <table className="w-full border-collapse text-body-03">
          <thead>
            <tr className="text-left text-grey-04">
              <th className="py-sm pr-sm font-medium">Goal</th>
              <th className="py-sm px-sm font-medium">Type</th>
              <th className="py-sm px-sm font-medium">Priority</th>
              <th className="py-sm px-sm font-medium">Target amount</th>
              <th className="py-sm px-sm font-medium">Target date</th>
              <th className="py-sm pl-sm font-medium" />
            </tr>
          </thead>
          <tbody>
            {goals.map((goal) =>
              editingId === goal.id ? (
                <tr key={goal.id} className="border-t border-grey-05">
                  <td colSpan={6} className="py-md">
                    <GoalForm
                      profileId={profileId}
                      initial={goal}
                      testidPrefix={`goal-edit-form-${goal.id}`}
                      onSaved={() => {
                        setEditingId(null);
                        reload();
                      }}
                      onCancel={() => setEditingId(null)}
                    />
                  </td>
                </tr>
              ) : (
                <GoalRow
                  key={goal.id}
                  profileId={profileId}
                  goal={goal}
                  onEdit={() => setEditingId(goal.id)}
                  onDeleted={reload}
                />
              ),
            )}
          </tbody>
        </table>
      )}

      <div className="border-t border-grey-05 pt-md">
        <div className="text-body-02 font-medium text-black mb-sm">Add goal</div>
        <GoalForm profileId={profileId} onSaved={reload} />
      </div>
    </div>
  );
}

function GoalRow({
  profileId,
  goal,
  onEdit,
  onDeleted,
}: {
  profileId: string;
  goal: FinancialGoal;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = () => {
    if (busy || goal.id == null) return;
    setBusy(true);
    setError(null);
    financialPositionApi
      .deleteGoal(profileId, goal.id)
      .then(() => onDeleted())
      .catch(() => {
        setError('Could not delete.');
        setBusy(false);
      });
  };

  return (
    <tr className="border-t border-grey-05 align-top" data-testid={`goal-row-${goal.id}`}>
      <td className="py-sm pr-sm text-black">{goal.title ?? '—'}</td>
      <td className="py-sm px-sm text-grey-04">{titleCase(goal.goalType)}</td>
      <td className="py-sm px-sm text-grey-04">{titleCase(goal.priority)}</td>
      <td className="py-sm px-sm text-grey-04">{fmtMoney(goal.targetAmount)}</td>
      <td className="py-sm px-sm text-grey-04">{goal.targetDate ?? '—'}</td>
      <td className="py-sm pl-sm">
        <div className="flex items-center gap-md">
          <button
            type="button"
            data-testid={`goal-edit-${goal.id}`}
            onClick={onEdit}
            disabled={busy}
            className="text-body-03 text-purple-primary hover:underline disabled:opacity-60"
          >
            Edit
          </button>
          <button
            type="button"
            data-testid={`goal-delete-${goal.id}`}
            onClick={remove}
            disabled={busy}
            className="text-body-03 text-error-text hover:underline disabled:opacity-60"
          >
            Delete
          </button>
          {error && <span className="text-body-04 text-error-text">{error}</span>}
        </div>
      </td>
    </tr>
  );
}

/**
 * Shared add/edit form. When {@code initial} is given it edits that row (PUT);
 * otherwise it creates a new one (POST). After a successful save the
 * create-form resets to a blank draft.
 */
function GoalForm({
  profileId,
  initial,
  onSaved,
  onCancel,
  testidPrefix = 'goal-new',
}: {
  profileId: string;
  initial?: FinancialGoal;
  onSaved: () => void;
  onCancel?: () => void;
  testidPrefix?: string;
}) {
  const isEdit = initial != null && initial.id != null;
  const [form, setForm] = useState<FinancialGoal>(initial ?? blankDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    if (saving || !form.title) return;
    setSaving(true);
    setError(null);
    const op =
      isEdit && initial?.id != null
        ? financialPositionApi.updateGoal(profileId, initial.id, form)
        : financialPositionApi.createGoal(profileId, form);
    op
      .then(() => {
        if (!isEdit) setForm(blankDraft());
        onSaved();
      })
      .catch(() => setError('Could not save the goal.'))
      .finally(() => setSaving(false));
  };

  return (
    <div data-testid={testidPrefix} className="flex flex-col gap-md">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-md">
        <label className="flex flex-col gap-xs text-body-04 text-grey-04">
          Goal
          <FormInput
            data-testid={`${testidPrefix}-title`}
            value={form.title ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, title: e.target.value === '' ? null : e.target.value }))
            }
          />
        </label>
        <label className="flex flex-col gap-xs text-body-04 text-grey-04">
          Type
          <Dropdown
            data-testid={`${testidPrefix}-goalType`}
            options={GOAL_TYPES.map((t) => ({ value: t, text: titleCase(t) }))}
            value={form.goalType ?? 'OTHER'}
            onChange={(value) => setForm((f) => ({ ...f, goalType: value }))}
          />
        </label>
        <label className="flex flex-col gap-xs text-body-04 text-grey-04">
          Priority
          <Dropdown
            data-testid={`${testidPrefix}-priority`}
            options={PRIORITIES.map((p) => ({ value: p, text: titleCase(p) }))}
            value={form.priority ?? 'MEDIUM'}
            onChange={(value) => setForm((f) => ({ ...f, priority: value }))}
          />
        </label>
        <label className="flex flex-col gap-xs text-body-04 text-grey-04">
          Target amount
          <FormInput
            type="number"
            data-testid={`${testidPrefix}-targetAmount`}
            value={form.targetAmount ?? ''}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                targetAmount: e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </label>
        <label className="flex flex-col gap-xs text-body-04 text-grey-04">
          Target date
          <FormInput
            type="date"
            data-testid={`${testidPrefix}-targetDate`}
            value={form.targetDate ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, targetDate: e.target.value === '' ? null : e.target.value }))
            }
          />
        </label>
      </div>
      {error && <Alert variant="error">{error}</Alert>}
      <div className="flex justify-end gap-md">
        {onCancel && (
          <Button
            variant="outlined"
            data-testid={`${testidPrefix}-cancel`}
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
        )}
        <Button
          data-testid={`${testidPrefix}-save`}
          onClick={save}
          disabled={saving || !form.title}
        >
          {isEdit ? 'Save' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
